import { INVITE_PATH, INVITE_RESET_MS, landsIn, nextIssue } from '../weekly';
import { onTick } from './clock';
import { $, text } from './dom';
import { track } from './events';

/** The invite button's word for a copied link, and what a screen reader hears. */
const COPIED = 'Link copied ✓';
const COPIED_SPOKEN = 'Link copied';

/**
 * The hero's Weekly Sonar card: "Next issue · Mon 19 Oct" and the countdown
 * to it, redrawn on the browser's own clock and then every tick, and the
 * invite link under the done message. The sign-up itself is signup()'s
 * (widgets.ts). Returns what stops its timers.
 */
export function weeklyCard(root: HTMLElement): () => void {
  const day = $(root, '[data-weekly-day]');
  const left = $(root, '[data-weekly-cd]');

  let issue = nextIssue(Date.now());
  const paint = () => {
    const now = Date.now();
    // Monday 08:00 came while the page was open: on to the next one.
    if (now >= issue.at) issue = nextIssue(now);
    text(day, issue.day);
    text(left, landsIn(issue.at - now));
  };
  paint();
  const stopTicking = onTick(paint);
  const stopInvite = invite(root);
  return () => {
    stopTicking();
    stopInvite();
  };
}

/**
 * "Copy invite link": the card's address on this site, raisesonar.com in
 * production, to the clipboard; "Link copied ✓" for 2.4 s, then the label.
 */
function invite(root: HTMLElement): () => void {
  const button = $<HTMLButtonElement>(root, '[data-invite]');
  const status = $(root, '[data-invite-status]');
  if (!button) return () => {};
  const label = button.textContent ?? '';
  let timer: number | undefined;

  const reset = () => {
    text(button, label);
    button.classList.remove('is-copied');
    text(status, '');
  };
  button.addEventListener('click', () => {
    void copy(`${window.location.origin}${INVITE_PATH}`, button).then((ok) => {
      if (!ok) return;
      window.clearTimeout(timer);
      text(button, COPIED);
      button.classList.add('is-copied');
      text(status, COPIED_SPOKEN);
      track('weekly_invite_copied', {});
      timer = window.setTimeout(reset, INVITE_RESET_MS);
    });
  });
  return () => window.clearTimeout(timer);
}

/**
 * The Clipboard API, or where it is missing or refused (a page not on
 * https, an older browser) a selected textarea and the copy command. Focus
 * goes back to `from` either way.
 */
async function copy(value: string, from: HTMLElement): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    /* the fallback below */
  }
  const area = document.createElement('textarea');
  area.value = value;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.append(area);
  area.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    area.remove();
    from.focus();
  }
}
